import { Stack, Text, Group, Badge, Anchor } from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  IconPhone,
  IconMail,
  IconMapPin,
  IconPrinter,
} from '@tabler/icons-react';
import './ComingSoon.css';

const PARTICLES = [
  { size: 6,  top: '15%', left: '10%', color: 'rgba(96, 165, 250, 0.08)', delay: '0s' },
  { size: 10, top: '27%', left: '24%', color: 'rgba(96, 165, 250, 0.10)', delay: '0.6s' },
  { size: 14, top: '39%', left: '38%', color: 'rgba(96, 165, 250, 0.12)', delay: '1.2s' },
  { size: 18, top: '51%', left: '52%', color: 'rgba(96, 165, 250, 0.14)', delay: '1.8s' },
  { size: 22, top: '63%', left: '66%', color: 'rgba(96, 165, 250, 0.16)', delay: '2.4s' },
  { size: 26, top: '75%', left: '80%', color: 'rgba(96, 165, 250, 0.18)', delay: '3.0s' },
];

export default function ComingSoon() {
  return (
    <div className="cs-page">
      {/* ============ HERO SECTION ============ */}
      <div className="cs-hero">
        {/* Ambient floating particles */}
        {PARTICLES.map((p, i) => (
          <div
            key={i}
            className="cs-particle"
            style={{
              width: p.size,
              height: p.size,
              background: p.color,
              top: p.top,
              left: p.left,
              animationDelay: p.delay,
              animationDuration: `${6 + i * 1.2}s`,
            }}
          />
        ))}

        {/* Orbiting rings */}
        <div
          className="cs-ring"
          style={{
            width: 550,
            height: 550,
            border: '1px solid rgba(148, 163, 184, 0.06)',
          }}
        />
        <div
          className="cs-ring cs-ring--inner"
          style={{
            width: 380,
            height: 380,
            border: '1px solid rgba(148, 163, 184, 0.04)',
          }}
        />

        <Stack align="center" gap="xl" style={{ zIndex: 1, maxWidth: 700, padding: '0 24px' }}>
          {/* Logo */}
          <div className="cs-logo">
            <div
              style={{
                background: '#f5f0e8',
                borderRadius: 28,
                padding: '28px 40px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
              }}
            >
              <img
                src="/logo.png"
                alt="Standard Produce Co."
                style={{
                  height: 160,
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
          </div>

          {/* Tagline */}
          <div className="cs-headline">
            <Text
              ta="center"
              size="md"
              c="rgba(226, 232, 240, 0.5)"
              fw={500}
              style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}
            >
              Setting the Standard Since 1910
            </Text>
          </div>

          {/* Headline */}
          <div className="cs-headline">
            <Text
              ta="center"
              fw={800}
              c="white"
              style={{
                fontSize: 'clamp(2.2rem, 6vw, 4rem)',
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
              }}
            >
              Something Fresh Is Growing
            </Text>
          </div>

          {/* Subtitle */}
          <div className="cs-subtitle">
            <Text
              ta="center"
              size="xl"
              c="rgba(226, 232, 240, 0.65)"
              fw={400}
              style={{ lineHeight: 1.7, maxWidth: 520 }}
            >
              We're building a brand new ordering experience.
              Fresh produce, real-time pricing, and smarter tools — all in one place.
            </Text>
          </div>

          {/* Feature pills */}
          <div className="cs-pills">
            <Group justify="center" gap="xs">
              {['Online Ordering', 'Real-Time Pricing', 'Delivery Tracking'].map(
                (feature) => (
                  <Badge
                    key={feature}
                    variant="outline"
                    size="lg"
                    radius="xl"
                    styles={{
                      root: {
                        borderColor: 'rgba(148, 163, 184, 0.25)',
                        color: 'rgba(226, 232, 240, 0.8)',
                        background: 'rgba(148, 163, 184, 0.06)',
                        backdropFilter: 'blur(10px)',
                        fontWeight: 500,
                        textTransform: 'none',
                        padding: '8px 16px',
                      },
                    }}
                  >
                    {feature}
                  </Badge>
                ),
              )}
            </Group>
          </div>

          {/* Launching soon badge */}
          <div className="cs-badge">
            <div className="cs-badge-inner">
              <Badge
                size="xl"
                radius="xl"
                variant="filled"
                styles={{
                  root: {
                    background: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
                    padding: '12px 32px',
                    fontSize: 14,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    boxShadow: '0 4px 30px rgba(96, 165, 250, 0.3)',
                  },
                }}
              >
                Launching Soon
              </Badge>
            </div>
          </div>

          {/* Links */}
          <div className="cs-pills">
            <Group justify="center" gap="md">
              <Anchor component={Link} to="/apply" c="rgba(226, 232, 240, 0.7)" size="sm" fw={500} underline="hover">
                Employment Application
              </Anchor>
              <Text c="rgba(148, 163, 184, 0.3)" size="sm">|</Text>
              <Anchor component={Link} to="/credit-application" c="rgba(226, 232, 240, 0.7)" size="sm" fw={500} underline="hover">
                Credit Application
              </Anchor>
              <Text c="rgba(148, 163, 184, 0.3)" size="sm">|</Text>
              <Anchor href="https://www.e-billexpress.com/ebpp/SPC" c="rgba(226, 232, 240, 0.7)" size="sm" fw={500} underline="hover">
                Pay Online
              </Anchor>
            </Group>
          </div>
        </Stack>
      </div>

      {/* ============ ABOUT SECTION ============ */}
      <div className="cs-section">
        <div className="cs-section-title">
          <Text size="sm" c="rgba(96, 165, 250, 0.8)" fw={600} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }} mb="xs">
            About Us
          </Text>
          <Text fw={700} c="white" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)' }}>
            Your Partner in Fresh
          </Text>
        </div>

        <Text
          ta="center"
          size="lg"
          c="rgba(226, 232, 240, 0.65)"
          fw={400}
          style={{ lineHeight: 1.8, maxWidth: 680, margin: '0 auto' }}
          mb="xl"
        >
          Nestled next to the beautiful Blue Ridge Mountains in Charlottesville, Virginia,
          Standard Produce Company has been servicing repeat and satisfied customers since 1910.
        </Text>

        <Text
          ta="center"
          size="md"
          c="rgba(226, 232, 240, 0.5)"
          fw={400}
          style={{ lineHeight: 1.8, maxWidth: 680, margin: '0 auto' }}
          mb="xl"
        >
          Currently serving Charlottesville, Harrisonburg, Richmond, Culpeper,
          Waynesboro, Staunton, and the surrounding areas.
        </Text>

      </div>

      {/* ============ CONTACT SECTION ============ */}
      <div className="cs-section" style={{ paddingTop: 0 }}>
        <div className="cs-section-title">
          <Text size="sm" c="rgba(96, 165, 250, 0.8)" fw={600} style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }} mb="xs">
            Contact Us
          </Text>
          <Text fw={700} c="white" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)' }}>
            Get in Touch
          </Text>
        </div>

        <div className="cs-contact-grid">
          {/* Phone */}
          <div className="cs-contact-card">
            <IconPhone size={28} color="rgba(96, 165, 250, 0.8)" style={{ marginBottom: 12 }} />
            <Text fw={600} c="rgba(226, 232, 240, 0.9)" size="sm" mb={4}>Phone</Text>
            <Text c="rgba(226, 232, 240, 0.65)" size="sm">
              Toll Free: 1-800-228-5171
            </Text>
            <Text c="rgba(226, 232, 240, 0.65)" size="sm">
              Local: (434) 295-5171
            </Text>
            <Group justify="center" gap={4} mt={4}>
              <IconPrinter size={14} color="rgba(226, 232, 240, 0.4)" />
              <Text c="rgba(226, 232, 240, 0.4)" size="xs">
                Fax: (434) 971-9808
              </Text>
            </Group>
          </div>

          {/* Email */}
          <div className="cs-contact-card">
            <IconMail size={28} color="rgba(96, 165, 250, 0.8)" style={{ marginBottom: 12 }} />
            <Text fw={600} c="rgba(226, 232, 240, 0.9)" size="sm" mb={4}>Email</Text>
            <Anchor href="mailto:info@standardproduce.com" c="rgba(96, 165, 250, 0.7)" size="sm" underline="hover">
              info@standardproduce.com
            </Anchor>
            <br />
            <Anchor href="mailto:orders@standardproduce.com" c="rgba(96, 165, 250, 0.7)" size="sm" underline="hover">
              orders@standardproduce.com
            </Anchor>
          </div>

          {/* Address */}
          <div className="cs-contact-card">
            <IconMapPin size={28} color="rgba(96, 165, 250, 0.8)" style={{ marginBottom: 12 }} />
            <Text fw={600} c="rgba(226, 232, 240, 0.9)" size="sm" mb={4}>Address</Text>
            <Text c="rgba(226, 232, 240, 0.65)" size="sm">
              140 Garrett Street
            </Text>
            <Text c="rgba(226, 232, 240, 0.65)" size="sm">
              Charlottesville, VA 22902
            </Text>
          </div>
        </div>
      </div>

      {/* ============ FOOTER ============ */}
      <div className="cs-bottom-footer">
        <Group justify="center" gap="lg" mb="xs">
          <Anchor component={Link} to="/apply" c="rgba(226, 232, 240, 0.5)" size="xs" fw={500} underline="hover">
            Careers
          </Anchor>
          <Anchor component={Link} to="/credit-application" c="rgba(226, 232, 240, 0.5)" size="xs" fw={500} underline="hover">
            Credit Application
          </Anchor>
          <Anchor component={Link} to="/login" c="rgba(226, 232, 240, 0.5)" size="xs" fw={500} underline="hover">
            Customer Portal
          </Anchor>
          <Anchor href="mailto:info@standardproduce.com" c="rgba(226, 232, 240, 0.5)" size="xs" fw={500} underline="hover">
            Email Us
          </Anchor>
          <Anchor href="tel:1-800-228-5171" c="rgba(226, 232, 240, 0.5)" size="xs" fw={500} underline="hover">
            1-800-228-5171
          </Anchor>
        </Group>
        <Text ta="center" size="xs" c="rgba(148, 163, 184, 0.35)">
          &copy; Standard Produce Company {new Date().getFullYear()} &middot; Setting the Standard Since 1910
        </Text>
      </div>
    </div>
  );
}
